import type { TOC } from '@ember/component/template-only';
import { fn, hash } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, gt, not, or } from 'ember-truth-helpers';
import GeneralButtonsLiveshopButton from 'lumen/components/general/buttons/liveshop-button';
import GeneralButtonsOtbSelfServiceButton from 'lumen/components/general/buttons/otb-self-service-button';
import GeneralCalendarsCalendarBase from 'lumen/components/general/calendars/calendar-base';
import GeneralDatepickersDatepickerWithCompare from 'lumen/components/general/datepickers/datepicker-with-compare';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralDropdownsTriggersTriggerStatic from 'lumen/components/general/dropdowns/triggers/trigger-static';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import HotelMarketInsightChartsChartOtbOverview from 'lumen/components/hotel/market-insight/charts/chart-otb-overview';
import HotelPricingAssistantModalsPricingAssistantUpsellModal from 'lumen/components/hotel/pricing-assistant/modals/pricing-assistant-upsell-modal';
import HotelPricingAssistantPageModeViewSwitcher from 'lumen/components/hotel/pricing-assistant/page-mode-view-switcher';
import HotelRatesCalendarsDaysDayRates from 'lumen/components/hotel/rates/calendars/days/day-rates';
import HotelRatesChartsChartRates from 'lumen/components/hotel/rates/charts/chart-rates';
import HotelRatesModalsCalendarSettingsModal from 'lumen/components/hotel/rates/modals/calendar-settings-modal';
import HotelRatesTablesTableRates from 'lumen/components/hotel/rates/tables/table-rates';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import HotelAndMarketRatesOtaSelectionsRatesSelections from 'lumen/components/hotel-and-market/rates/ota-selections/rates-selections';
import type HotelRatesController from 'lumen/controllers/hotel/rates';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';

interface HotelRatesSignature {
  Args: {
    controller: HotelRatesController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row justify-between">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          <GeneralDatepickersDatepickerWithCompare
            {{! @glint-expect-error: Incorrect type }}
            @compareDisabledTooltipText={{@controller.compareDisabledTooltipText}}
            @compareType={{@controller.compareType}}
            @compareValue={{@controller.compareValue}}
            @displayEvents={{true}}
            @isCompareDisabled={{@controller.isCompareDisabled}}
            @isMonthInPast={{@controller.isPast}}
            @isTableView={{@controller.isTableView}}
            @range={{@controller.dateRange}}
            @rangeChanged={{@controller.updateSelectedMonth}}
            @selectStandardRange={{true}}
            @selectionsChanged={{@controller.selectionsChanged}}
            @standardRange="month"
          />
        </div>

        {{#if (hasFeature "app.pricing_assistant")}}
          <div
            class="pricing-assistant-page-mode-switcher flex flex-row items-center justify-center"
          >
            <HotelPricingAssistantPageModeViewSwitcher
              @pageMode={{@controller.pageMode}}
              @updatePageMode={{@controller.updatePageMode}}
            />
          </div>
        {{/if}}

        <div class="flex flex-row items-center gap-200">
          <div class="flex flex-row items-center gap-200">
            <PrismViewSwitcher
              @activeButtonId={{@controller.view}}
              @onSetActiveButtonId={{@controller.switchTab}}
              as |ViewSwitcher|
            >
              {{! @glint-expect-error: Incorrect type }}
              <ViewSwitcher.button @icon="calendar-message" @id="calendar" />
              {{! @glint-expect-error: Incorrect type }}
              <ViewSwitcher.button
                @icon="view-chart"
                @id="chart"
                class="ic-product-tour-rates-chart-view"
              />
              {{! @glint-expect-error: Incorrect type }}
              <ViewSwitcher.button
                @icon="view-table"
                @id="table"
                class="ic-product-tour-rates-table-view"
              />
            </PrismViewSwitcher>
            <GeneralDropdownsDropdownExport
              @export={{@controller.handleExport}}
              @exportChoices={{@controller.exportChoices}}
              @isScheduleDisabled={{@controller.isScheduleDisabled}}
              class="ic-product-tour-rates-export"
            />
          </div>

          <GeneralButtonsLiveshopButton
            @hasLiveShop={{@controller.liveupdateEnabled}}
            @noDataForSelection={{@controller.noDataForSelection}}
            @onClick={{@controller.startUpdate}}
            {{! @glint-expect-error: Incorrect type }}
            @platform={{@controller.platform}}
            @selectedOTA={{@controller.selectedOTA}}
          />
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div
      class="content-wrapper flex items-center justify-between pt-200 pb-200"
    >
      <HotelAndMarketRatesOtaSelectionsRatesSelections
        {{! @glint-expect-error: Incorrect type }}
        @activeView={{@controller.view}}
        @compareDisabledTooltipText={{@controller.compareDisabledTooltipText}}
        @compareType={{@controller.compareType}}
        @compareValue={{@controller.compareValue}}
        @isCompareDisabled={{@controller.isCompareDisabled}}
        @isMonthInPast={{@controller.isPast}}
        @isRoomtypeRequestLoading={{@controller.isRoomtypeRequestLoading}}
        @isTableViewActive={{@controller.isTableView}}
        @monthMoment={{@controller.monthMoment}}
        @platform={{@controller.platform}}
        @rateParam={{@controller.rateParam}}
        @selectedDayComponent={{HotelRatesCalendarsDaysDayRates}}
        @selectedLos={{@controller.los}}
        @selectedMaxPersons={{@controller.maxPersons}}
        @selectedMealPlan={{@controller.mealPlan}}
        @selectedMembershipType={{@controller.membershipType}}
        @selectedOTAId={{@controller.otaId}}
        @selectedPlatform={{@controller.platform}}
        @selectedRoomType={{@controller.roomType}}
        @selectionsChanged={{@controller.selectionsChanged}}
        @setCompsetId={{@controller.setCompsetId}}
        @type="rates"
        {{! @glint-expect-error: Incorrect type }}
        {{didUpdate
          @controller.doRefresh
          @controller.hotelAndMarketService.availableCompsets
        }}
      />

      {{#if
        (or
          @controller.isCalendarView
          @controller.isTableView
          @controller.isValidMonthToShowUpdateTime
        )
      }}
        <div class="flex flex-row items-center gap-200 text-nowrap">
          {{#if @controller.isCalendarView}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="sliders"
              @label={{t "rates.calendar_settings"}}
              @onClick={{fn @controller.showCalendarSettingsModal true}}
            />
          {{else if @controller.isTableView}}
            <PrismComboSelectInput
              {{! @glint-expect-error: Incorrect type }}
              @onChange={{@controller.updateTableSettings}}
              {{! @glint-expect-error: Incorrect type }}
              @options={{@controller.tableSettingOptions}}
              @unsafe_dropdownClass="multi-select-competitors--with-rate-comparison-layout-setting"
              @unsafe_hasDropdownMaxHeight={{false}}
              @unsafe_triggerComponent={{component
                GeneralDropdownsTriggersTriggerStatic
                buttonType="secondary"
                iconBefore="sliders"
                label=(t "general.table_settings")
              }}
              {{! @glint-expect-error: Incorrect type }}
              @value={{@controller.selectedTableSettingOptions}}
            />
          {{/if}}

          {{#if @controller.isValidMonthToShowUpdateTime}}
            <div class="update-timestamp text-200--regular">
              {{#if @controller.isLoading}}
                <div
                  class="update-timestamp-loading-placeholder loading-pulse"
                ></div>
              {{else if @controller.timestamp}}
                {{t "general.updated"}}
                {{@controller.timestamp}}
              {{/if}}
            </div>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    <div class="content-wrapper main-content--new-layout">
      {{#if @controller.isLoading}}
        <PrismLoadingBar
          @isFixed={{true}}
          @positionTop={{@controller.mainLoadingBarPositionTop}}
        />
      {{/if}}

      {{! Banner height can change on container resizing or when user dismisses the banner. We want the effective container height to be updated in those cases }}
      <div
        {{didInsert @controller.setBannerHeight}}
        {{didUpdate
          @controller.setBannerHeight
          @controller.containerHeight
          @controller.containerWidth
          @controller.shortTermRentalUpsell.shortTermRentalUpsellData
          @controller.myotbSs.isOtbSelfServiceUpsellBannerVisible
          @controller.isPAUpsellBannerVisible
        }}
      >
        {{#if @controller.isRatesUpsellBannerVisible}}
          {{! In case both STR and OTB upsell available, we only show the STR banner }}
          {{#if
            @controller.shortTermRentalUpsell.canShowShortTermRentalUpsellBanner
          }}
            {{! Wait until the STR upsell data is available }}
            {{#if @controller.shortTermRentalUpsell.shortTermRentalUpsellData}}
              <div class="mb-400">
                <PrismBanner
                  @bannerMessage={{t
                    "short_term_rental.upsell.banner.message"
                    properties=@controller.shortTermRentalUpsell.shortTermRentalProperties
                    relevantProperties=@controller.shortTermRentalUpsell.shortTermRentalRelevantProperties
                  }}
                  @bannerStyle="brand"
                  @bannerTitle={{t "short_term_rental.upsell.banner.title"}}
                  @hasCta={{true}}
                  @position="island"
                  @primaryAction={{hash
                    action=@controller.shortTermRentalUpsell.onShortTermRentalUpsellBannerPrimaryButtonClick
                    label=(t "general.learn_more")
                  }}
                  @secondaryAction={{hash
                    action=@controller.shortTermRentalUpsell.onShortTermRentalUpsellBannerDismiss
                    label=(t "general.dismiss")
                  }}
                />
              </div>
            {{/if}}
          {{else if @controller.isPAUpsellBannerVisible}}
            <div class="mb-400">
              <PrismBanner
                @bannerMessage={{t "pricing_assistant.upsell.banner.title"}}
                @bannerStyle="brand"
                @close={{@controller.onPricingAssistantUpsellBannerDismiss}}
                @hasCta={{true}}
                @icon="rates-lowest"
                @isOneLine={{true}}
                @link={{hash
                  text=(t "general.learn_more")
                  url="https://www.mylighthouse.com/ai-automation/pricing-manager"
                }}
                @primaryAction={{hash
                  action=@controller.onPricingAssistantUpsellBannerPrimaryButtonClick
                  label=(t "pricing_assistant.upsell_banner.start_free_trial")
                }}
              />
            </div>
          {{else if @controller.myotbSs.isOtbSelfServiceUpsellBannerVisible}}
            <div class="mb-400">
              <GeneralEmptyStateOtbBanner
                {{! @glint-expect-error: Incorrect type }}
                @otbSelfServiceEntryPoint={{@controller.emptyStateOtbBannerEntryPoint}}
                @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
              />
            </div>
          {{/if}}
        {{/if}}
      </div>

      {{#if @controller.isCalendarView}}
        <div class="card-tile card-tile--overflow-hidden">
          <GeneralCalendarsCalendarBase
            @calenderHeaderClass="ins-calendar-header--new-layout"
            @containerHeight={{@controller.containerHeight}}
            @dayComponent={{HotelRatesCalendarsDaysDayRates}}
            @isLoading={{@controller.isPeriodsRequestLoading}}
            @los={{@controller.los}}
            @message={{@controller.noDataForSelection}}
            @minDayComponentHeight={{95}}
            {{! @glint-expect-error: Incorrect type }}
            @model={{@controller.periods}}
            {{! @glint-expect-error: Incorrect type }}
            @otaId={{@controller.otaId}}
            @reservedHeight={{@controller.calendarReservedHeight}}
          >
            <:dayComponent as |Component|>
              {{! @glint-expect-error: Incorrect type }}
              <Component.day
                @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
                @hasDayLiveShop={{@controller.dayLiveupdateEnabled}}
                @isBestFlex={{@controller.isBestFlex}}
                @monthMoment={{@controller.monthMoment}}
                @otaSupported={{@controller.selectedOTA.hasLiveShop}}
                @params={{@controller.params}}
                @ratesEvolutionOrBookingCurveEnabled={{@controller.ratesEvolutionOrBookingCurveEnabled}}
                @redirectToDayDetail={{@controller.redirectToDayDetail}}
                @selectedOTA={{@controller.selectedOTA}}
              />
            </:dayComponent>
          </GeneralCalendarsCalendarBase>
        </div>
      {{else if @controller.isTableView}}
        {{#if @controller.showOTBSelfServiceButton}}
          <div class="mb-200 flex items-center justify-end">
            <GeneralButtonsOtbSelfServiceButton
              @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.RATES_TABLE}}
              @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
            />
          </div>
        {{/if}}

        <div class="card-tile card-tile--overflow-hidden">
          <HotelRatesTablesTableRates
            @compareType={{@controller.compareType}}
            @compareValue={{@controller.compareValue}}
            {{! @glint-expect-error: Incorrect type }}
            @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
            @containerHeight={{@controller.containerHeight}}
            @dayLiveupdateEnabled={{@controller.dayLiveupdateEnabled}}
            @hiddenColumnIds={{@controller.hiddenColumnIdsWithFallback}}
            @isCompareDisabled={{@controller.isCompareDisabled}}
            @isCurrentOrFutureDate={{@controller.isCurrentOrFutureDate}}
            @isLoading={{@controller.isLoading}}
            @isMonthInPast={{@controller.isPast}}
            {{! @glint-expect-error: Incorrect type }}
            @message={{@controller.noDataForSelection}}
            @monthOnlyModel={{@controller.monthOnlyModel}}
            @otbOutOfSyncUtil={{@controller.otbOutOfSyncUtil}}
            {{! @glint-expect-error: Incorrect type }}
            @requestParams={{@controller.params}}
            @reservedHeight={{@controller.calendarReservedHeight}}
            {{! @glint-expect-error: Incorrect type }}
            @selectedOTA={{@controller.selectedOTA}}
            @showOTBSelfServiceButton={{@controller.showOTBSelfServiceButton}}
            @tableComparisonLayout={{@controller.tableComparisonLayout}}
          />
        </div>
      {{else if @controller.isChartView}}
        {{#if @controller.isPeriodsRequestLoading}}
          <div class="main-content-progress-bar-wrapper">
            <PrismLoadingBar />
          </div>
        {{/if}}
        <PrismCard class="card-tile">
          <:header as |Header|>
            <Header.title>
              {{t "rate_insight.block_title.rate_spread"}}
            </Header.title>
          </:header>

          <:content>
            <HotelRatesChartsChartRates
              {{! @glint-expect-error: Incorrect type }}
              @activeCompset={{@controller.hotelAndMarketService.activeCompset}}
              @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
              @isBestFlex={{@controller.isBestFlex}}
              @message={{@controller.noDataForSelection}}
              @params={{@controller.params}}
              @periods={{@controller.monthOnlyModel}}
              @ratesEvolutionOrBookingCurveEnabled={{@controller.ratesEvolutionOrBookingCurveEnabled}}
              @redirectToDayDetail={{@controller.redirectToDayDetail}}
              @selectedOTA={{@controller.selectedOTA}}
            />
          </:content>
        </PrismCard>

        {{#if
          (or
            (hasFeature "pms.otb.view")
            (and
              @controller.access.hasShortTermRentalAccess
              (not @controller.access.hasShortTermRentalOnlyStrTab)
            )
          )
        }}
          <PrismCard class="mt-400">
            <:header as |Header|>
              <Header.title>
                {{t "general.occupancy"}}
              </Header.title>
            </:header>

            <:headerAfter>
              {{#if @controller.otbOutOfSyncUtil.isOtbOutOfSync}}
                <GeneralIconWithTooltip
                  @iconName="sync-disabled"
                  @side="top"
                  {{! @glint-expect-error: Incorrect type }}
                  @tooltipContent={{@controller.otbOutOfSyncUtil.otbAndMarketOtbUpdatedString}}
                />
              {{/if}}
            </:headerAfter>

            <:content>
              {{! @glint-expect-error: Incorrect type }}
              <HotelMarketInsightChartsChartOtbOverview
                @alignWithRatesChart={{true}}
                @isDailyOverview={{true}}
                @offsetTooltipHeight={{false}}
                @onChartClicked={{@controller.onOccupancyChartClicked}}
                @sortedDays={{@controller.sortedDays}}
              />
            </:content>
          </PrismCard>
        {{/if}}
      {{/if}}
    </div>
  </GeneralPageMain>

  <GeneralReportScheduleReportsModals
    @canCreateNewUser={{can "hotel.reports.create.other.new"}}
    @canCreateOtherReports={{can "hotel.reports.create.other.existing"}}
    @compareType={{@controller.compareType}}
    @compareValue={{@controller.compareValue}}
    @compsetAvailable={{gt
      @controller.hotelAndMarketService.availableCompsets.length
      1
    }}
    @compsetId={{@controller.compsetId}}
    @isShowingScheduleModal={{@controller.scheduleReport}}
    @los={{@controller.los}}
    @maxPersons={{@controller.maxPersons}}
    @mealPlan={{@controller.mealPlan}}
    @membershipType={{@controller.membershipType}}
    @onCloseScheduleModal={{@controller.closeScheduleModal}}
    @otaId={{@controller.otaId}}
    {{! @glint-expect-error: Incorrect type }}
    @platform={{@controller.platform}}
    @rateParam={{@controller.rateParam}}
    @roomType={{@controller.roomType}}
    @selectedOTA={{@controller.selectedOTA}}
    @type="rates"
  />

  {{#if @controller.isShowingCalendarSettingsModal}}
    <HotelRatesModalsCalendarSettingsModal
      @closeModal={{fn @controller.showCalendarSettingsModal false}}
    />
  {{/if}}

  {{#if @controller.isShowingPAUpsellModal}}
    <HotelPricingAssistantModalsPricingAssistantUpsellModal
      @closeModal={{fn @controller.showPAUpsellModal false}}
    />
  {{/if}}

  {{outlet}}
</template> satisfies TOC<HotelRatesSignature>;
